import axios from 'axios';
import * as actionTypes from './constants';
import {
  setWarningMessage,
  setErrorMessage,
  setSuccessMessage
} from '../messages/actions';
import { socketConnect } from '../messages/actions';
import { getSelectedHotelMappedId } from '../hotels/selectors';
import {
  GET_OCCUPANCIES_REQUEST,
  GET_OCCUPANCIES_SUCCESS,
  GET_OCCUPANCIES_REJECT
} from '../rooms/occupancy/constants';
import moment from 'moment';
import { isToday } from '../utils/helpers/date';
import { getRoomGroups } from '../rooms/groups/actions';
import { getRooms } from '../rooms/actions';

export const changeStep = step => ({
  type: actionTypes.STEP_CHANGE,
  payload: step
});

export const clearSteps = () => ({
  type: actionTypes.CLEAR_STEPS
});
export const setLogin = login => ({
  type: actionTypes.SET_LOGIN,
  login
});

export const setPassword = password => ({
  type: actionTypes.SET_PASSWORD,
  password
});
export const selectHotel = selected => ({
  type: actionTypes.SELECT_HOTEL,
  selected
});
const STEPS = 5;
const STEP = 100 / STEPS;

export const synchronize = () => {
  return dispatch => {
    return dispatch(authenticate());
  };
};

export const authenticate = () => {
  return (dispatch, getState) => {
    const { username, password, hotels, selected } = getState().synchronization;
    if (!username) {
      dispatch(setWarningMessage('synchronization:warningUsernameText'));
      return Promise.reject('');
    }
    if (!password) {
      dispatch(setWarningMessage('synchronization:warningPasswordText'));
      return Promise.reject('');
    }
    if (hotels.length > 0 && selected.length === 0) {
      dispatch(setWarningMessage('synchronization:warningHotelsText'));
      return Promise.reject('');
    }
    dispatch({
      type: actionTypes.AUTHENTICATE_SYNC_USER_REQUEST
    });
    dispatch(clearSteps());
    dispatch(
      changeStep({
        text: 'synchronization:authenticationText',
        progress: STEP
      })
    );
    return axios
      .post('/authenticate', { username, password })
      .then(response => {
        dispatch({
          type: actionTypes.AUTHENTICATE_SYNC_USER_SUCCESS,
          hotels: response.data
        });
        return dispatch(syncUser());
      })
      .catch(err => {
        dispatch(setErrorMessage('synchronization:invalidLoginOrPasswordText'));
        dispatch({ type: actionTypes.AUTHENTICATE_SYNC_USER_REJECT });
      });
  };
};

export const syncUser = () => {
  return (dispatch, getState) => {
    const { user } = getState().account;
    const { username, password, kWhotelUserId } = getState().synchronization;

    dispatch({
      type: actionTypes.SYNC_USER_REQUEST
    });
    dispatch(
      changeStep({
        text: 'synchronization:synchronizingUserText',
        progress: STEP
      })
    );
    return axios
      .post('/kwhotel/user', {
        user,
        syncUser: { username, password, kWhotelUserId }
      })
      .then(response => {
        dispatch({
          type: actionTypes.SYNC_USER_SUCCESS,
          user: response.data.user,
          syncUser: response.data.syncUser
        });
        return dispatch(syncHotels());
      })
      .catch(err => {
        dispatch(setErrorMessage(err.response ? err.response.data : err.data));
        dispatch({ type: actionTypes.SYNC_USER_REJECT });
      });
  };
};

export const syncHotels = () => {
  return (dispatch, getState) => {
    const { userId } = getState().account.user;
    const { kWhotelUserId, selected } = getState().synchronization;

    dispatch({
      type: actionTypes.SYNC_HOTEL_REQUEST
    });

    dispatch(
      changeStep({
        text: 'synchronization:synchronizingHotelText',
        progress: STEP
      })
    );
    return axios
      .post('/kwhotel/hotel', { hotels: selected, kWhotelUserId, userId })
      .then(response => {
        dispatch({
          type: actionTypes.SYNC_HOTEL_SUCCESS,
          hotels: response.data
        });
        dispatch(socketConnect());
        return dispatch(syncRoomGroups());
      })
      .catch(err => {
        dispatch(setErrorMessage(err.response ? err.response.data : err.data));
        dispatch({ type: actionTypes.SYNC_HOTEL_REJECT });
      });
  };
};

export const syncRoomGroups = () => {
  return async (dispatch, getState) => {
    const { username, password } = getState().synchronization;
    const hotels = getState().hotels.data;
    dispatch({
      type: actionTypes.SYNC_ROOM_GROUPS_REQUEST
    });

    for (let hotel of hotels) {
      try {
        await axios.get('/kwhotel/groups', {
          headers: {
            username,
            password,
            syncHotelId: hotel.hotelMapping.hotelMapId,
            hotelId: hotel.hotelId
          }
        });

        dispatch(
          changeStep({
            text: 'synchronization:synchronizingRoomGroupText',
            progress: STEP / hotels.length
          })
        );
      } catch (err) {
        dispatch(setErrorMessage(err.response ? err.response.data : err.data));
        dispatch({ type: actionTypes.SYNC_ROOM_GROUPS_REJECT });
      }
    }
    dispatch({
      type: actionTypes.SYNC_ROOM_GROUPS_SUCCESS
    });
    return dispatch(syncRooms());
  };
};

export const syncRooms = () => {
  return async (dispatch, getState) => {
    const { username, password } = getState().synchronization;
    const hotels = getState().hotels.data;
    dispatch({
      type: actionTypes.SYNC_ROOMS_REQUEST
    });

    try {
      for (let hotel of hotels) {
        await axios.get('/kwhotel/rooms', {
          headers: {
            username,
            password,
            syncHotelId: hotel.hotelMapping.hotelMapId,
            hotelId: hotel.hotelId
          }
        });
        dispatch(
          changeStep({
            text: 'synchronization:synchronizingRoomsText',
            progress: STEP / hotels.length
          })
        );
      }
    } catch (err) {
      dispatch(setErrorMessage(err.response ? err.response.data : err.data));
      dispatch({ type: actionTypes.SYNC_ROOMS_REJECT });
    }
    dispatch({
      type: actionTypes.SYNC_ROOMS_SUCCESS
    });
  };
};

export const getSyncUser = () => {
  return (dispatch, getState) => {
    const hotelId = getState().hotels.selected;
    dispatch({
      type: actionTypes.GET_SYNC_USER_REQUEST
    });
    return axios
      .get('/synchronization/' + hotelId)
      .then(response => {
        dispatch({
          type: actionTypes.GET_SYNC_USER_SUCCESS,
          payload: response.data
        });
      })
      .catch(err => dispatch({ type: actionTypes.GET_SYNC_USER_REJECT }));
  };
};

export const getSyncedOccupancy = () => {
  return (dispatch, getState) => {
    const { date } = getState().occupancy;
    if (isToday(date)) {
      return dispatch(getSyncedRoomsInfoNow());
    } else {
      return dispatch(getSyncedRoomsInfoForDate());
    }
  };
};

const getSyncedRoomsInfoNow = () => {
  return (dispatch, getState) => {
    const { username, password, scheduledJob } = getState().synchronization;
    const syncHotelId = getSelectedHotelMappedId(getState());
    const hotelId = getState().hotels.selected;
    if (username === '' && password === '' && hotelId === '') return;
    dispatch({ type: GET_OCCUPANCIES_REQUEST });
    return axios
      .get('/kwhotel/room-info', {
        headers: {
          username,
          password,
          syncHotelId,
          hotelId
        },
        params: {
          date: moment(new Date()).format('YYYY-MM-DD')
        }
      })
      .then(response => {
        dispatch({
          type: GET_OCCUPANCIES_SUCCESS,
          occupancies: response.data
        });
        if (scheduledJob === undefined) {
          dispatch(scheduleGetRoomsInfoJob());
        }
      })
      .catch(err => {
        dispatch({ type: GET_OCCUPANCIES_REJECT });
      });
  };
};

const getSyncedRoomsInfoForDate = () => {
  return (dispatch, getState) => {
    const { username, password } = getState().synchronization;
    const syncHotelId = getSelectedHotelMappedId(getState());
    const { date } = getState().occupancy;
    const hotelId = getState().hotels.selected;
    if (username === '' && password === '' && hotelId === '') return;
    dispatch({ type: GET_OCCUPANCIES_REQUEST });
    return axios
      .get('/kwhotel/room-info-preview', {
        headers: {
          username,
          password,
          syncHotelId,
          hotelId
        },
        params: {
          date: date.format('YYYY-MM-DD')
        }
      })
      .then(response =>
        dispatch({
          type: GET_OCCUPANCIES_SUCCESS,
          occupancies: response.data
        })
      )
      .catch(err => {
        dispatch({ type: GET_OCCUPANCIES_REJECT, err });
      });
  };
};

export const scheduleGetRoomsInfoJob = () => {
  const INTERVAL_TIME = 5 * 60 * 1000;
  return (dispatch, getState) => {
    const { username, password } = getState().synchronization;
    if (!username || !password) return;
    const interval = setInterval(() => {
      dispatch(getSyncedOccupancy());
    }, INTERVAL_TIME);

    dispatch({
      type: actionTypes.SCHEDULE_GETTING_ROOM_INFO,
      payload: interval
    });
  };
};

export const clearScheduledJob = () => {
  return (dispatch, getState) => {
    const { scheduledJob } = getState().synchronization;

    clearInterval(scheduledJob);
  };
};

export const saveRoomNote = roomNote => {
  roomNote.date = roomNote.date.format('YYYY-MM-DD');
  return (dispatch, getState) => {
    const { username, password } = getState().synchronization;
    const syncHotelId = getSelectedHotelMappedId(getState());
    const hotelId = getState().hotels.selected;
    if (roomNote.roomInfoMapId > 0)
      return axios.put(
        `/kwhotel/room-info/${roomNote.roomInfoMapId}`,
        roomNote,
        {
          headers: {
            username,
            password,
            syncHotelId,
            hotelId
          }
        }
      );
    else
      return axios.post('/kwhotel/room-info/', roomNote, {
        headers: {
          username,
          password,
          syncHotelId,
          hotelId
        }
      });
  };
};

export const syncRoomGroupsAndRooms = () => {
  return async dispatch => {
    try {
      await dispatch(syncRoomGroups());
      await dispatch(getRoomGroups());
      await dispatch(getRooms());
    } catch (err) {
      dispatch(setErrorMessage('synchronization:synchronizingErrorText'));
    }
  };
};



// WEBPACK FOOTER //
// ./src/synchronization/actions.js